import { DEG, RAD, normalizeAzimuth } from "@summit/core";

export interface Orientation {
  /** Compass heading the device is looking at (camera axis, or top edge when flat), degrees. */
  heading: number;
  /** Elevation angle of the camera axis, degrees (negative = looking down). */
  pitch: number;
  /** True if the device is held roughly upright (camera pointing near the horizon). */
  upright: boolean;
  absolute: boolean;
}

type IOSOrientationEvent = DeviceOrientationEvent & { webkitCompassHeading?: number };

/**
 * Convert W3C alpha/beta/gamma into the world-space direction of the camera axis.
 * World frame: x east, y north, z up. Device frame: x right, y top, z out of the screen.
 */
export function orientationToLook(alpha: number, beta: number, gamma: number, screenAngle = 0): { heading: number; pitch: number; upright: boolean } {
  const a = alpha * DEG;
  const b = beta * DEG;
  const g = gamma * DEG;
  const cA = Math.cos(a), sA = Math.sin(a);
  const cB = Math.cos(b), sB = Math.sin(b);
  const cG = Math.cos(g), sG = Math.sin(g);
  // Rotation matrix R = Rz(alpha) · Rx(beta) · Ry(gamma) (W3C spec order), rows = world axes.
  const m = [
    [cA * cG - sA * sB * sG, -cB * sA, cA * sG + cG * sA * sB],
    [cG * sA + cA * sB * sG, cA * cB, sA * sG - cA * cG * sB],
    [-cB * sG, sB, cB * cG],
  ];
  // Camera looks along device -z.
  const cam = { x: -m[0][2], y: -m[1][2], z: -m[2][2] };
  const upright = Math.abs(cam.z) < 0.75;
  if (upright) {
    return { heading: normalizeAzimuth(Math.atan2(cam.x, cam.y) * RAD), pitch: Math.asin(Math.max(-1, Math.min(1, cam.z))) * RAD, upright };
  }
  // Flat on a table (or pointed at the ground): use the screen's "up" edge, rotated by the screen orientation.
  const s = screenAngle * DEG;
  const ux = Math.cos(s), uy = Math.sin(s); // device-space up vector for the current screen rotation: rotate (0,1)
  const dx = -uy, dy = ux; // (0,1) rotated by -screenAngle
  const top = { x: m[0][0] * dx + m[0][1] * dy, y: m[1][0] * dx + m[1][1] * dy };
  return { heading: normalizeAzimuth(Math.atan2(top.x, top.y) * RAD), pitch: 0, upright };
}

/** Circular exponential smoothing for headings. */
export class HeadingFilter {
  private sin = 0;
  private cos = 0;
  private started = false;
  constructor(private alpha = 0.25) {}
  push(heading: number): number {
    const r = heading * DEG;
    if (!this.started) {
      this.sin = Math.sin(r);
      this.cos = Math.cos(r);
      this.started = true;
    } else {
      this.sin += (Math.sin(r) - this.sin) * this.alpha;
      this.cos += (Math.cos(r) - this.cos) * this.alpha;
    }
    return normalizeAzimuth(Math.atan2(this.sin, this.cos) * RAD);
  }
  reset(): void {
    this.started = false;
  }
}

export type OrientationListener = (o: Orientation) => void;

/** Subscribe to device orientation. Resolves with an unsubscribe function, or throws if unavailable/denied. */
export async function watchOrientation(listener: OrientationListener): Promise<() => void> {
  if (typeof DeviceOrientationEvent === "undefined") throw new Error("This device has no orientation sensors.");
  const DOE = DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<"granted" | "denied"> };
  if (typeof DOE.requestPermission === "function") {
    const state = await DOE.requestPermission();
    if (state !== "granted") throw new Error("Compass permission was denied.");
  }
  const filter = new HeadingFilter(0.3);
  const pitchFilter = { v: 0, started: false };
  let absoluteSeen = false;

  const handle = (ev: IOSOrientationEvent, absolute: boolean) => {
    if (ev.alpha == null || ev.beta == null || ev.gamma == null) return;
    if (absolute) absoluteSeen = true;
    else if (absoluteSeen) return; // prefer the absolute stream when both fire
    let alpha = ev.alpha;
    let abs = absolute || ev.absolute === true;
    if (typeof ev.webkitCompassHeading === "number" && !Number.isNaN(ev.webkitCompassHeading)) {
      alpha = 360 - ev.webkitCompassHeading; // iOS: alpha is relative; substitute the compass heading
      abs = true;
    }
    const screenAngle = (screen.orientation?.angle ?? (window as unknown as { orientation?: number }).orientation ?? 0) as number;
    const look = orientationToLook(alpha, ev.beta, ev.gamma, screenAngle);
    const heading = filter.push(look.heading);
    if (!pitchFilter.started) {
      pitchFilter.v = look.pitch;
      pitchFilter.started = true;
    } else pitchFilter.v += (look.pitch - pitchFilter.v) * 0.3;
    listener({ heading, pitch: pitchFilter.v, upright: look.upright, absolute: abs });
  };
  const onAbs = (e: Event) => handle(e as IOSOrientationEvent, true);
  const onRel = (e: Event) => handle(e as IOSOrientationEvent, false);
  if ("ondeviceorientationabsolute" in window) window.addEventListener("deviceorientationabsolute", onAbs);
  window.addEventListener("deviceorientation", onRel);
  return () => {
    window.removeEventListener("deviceorientationabsolute", onAbs);
    window.removeEventListener("deviceorientation", onRel);
  };
}

export interface Position {
  lat: number;
  lon: number;
  altitude: number | null;
  accuracy: number;
}

export function getPosition(timeout = 15000): Promise<Position> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocation is not available."));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, altitude: p.coords.altitude, accuracy: p.coords.accuracy }),
      (e) => reject(new Error(e.code === 1 ? "Location permission was denied." : "Could not determine your position.")),
      { enableHighAccuracy: true, timeout, maximumAge: 60_000 },
    );
  });
}

export async function startCamera(video: HTMLVideoElement): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) throw new Error("Camera is not available in this browser.");
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
    audio: false,
  });
  video.srcObject = stream;
  await video.play().catch(() => {});
  return stream;
}

export function stopCamera(video: HTMLVideoElement): void {
  const stream = video.srcObject as MediaStream | null;
  stream?.getTracks().forEach((t) => t.stop());
  video.srcObject = null;
}
